// services/ChatService.js
import {
  collection,
  doc,
  addDoc,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  setDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  limit,
  orderBy
} from 'firebase/firestore'
import { v4 as uuidv4 } from 'uuid'
import { db } from '../firebase'

// export const addMessageToChat = async (chatId, messageData) => {
//   try {
//     const messageRef = collection(db, 'chats', chatId, 'messages')
//     // Add message exactly as in Flutter - no extra fields
//     const docRef = await addDoc(messageRef, messageData)

//     return docRef.id
//   } catch (error) {
//     console.error('Error adding message to chat:', error)
//     throw error
//   }
// }


export const addMessageToChat = async (chatId, messageData) => {
  try {
    const messageRef = doc(db, 'chats', chatId, 'messages', messageData.messageId)

    await setDoc(messageRef, messageData)
    await updateDoc(doc(db, 'chats', chatId), {
      lastMessage: messageData.message,
      lastMessageId: messageData.messageId,
      lastMessageSenderId: messageData.senderId,
      lastUpdatedAt: new Date().toISOString(),
      updateTimestamp: new Date().toISOString(),
    })

    return messageData.messageId
  } catch (error) {
    console.error('Error adding message to chat:', error)
    throw error
  }
}


// Helper to create message object in your format
export const createPinMessage = (
  pinData,
  userData,
  incident,
  attachments = [],
  pinId,
  trackId,
  messageLabel = 'Sent Track Marker'
) => {
  const tempId = uuidv4()
  const timestamp = new Date().toISOString()

  const mappedAttachments = Array.isArray(attachments)
    ? attachments.map((file) => ({
      url: file.photo_url,
      fileType: file?.fileType?.split('/')[1] || 'jpg',
      fileSize: `${file?.fileSize || 0}`,
      thumbnail: file.thumbnail || null
    }))
    : []

  return {
    createdAt: null,
    deletedBy: [],
    messageId: tempId,
    message: messageLabel,
    files: {
      attachments: mappedAttachments
    },
    pinData: {
      latitude: pinData.latitude.toString(),
      longitude: pinData.longitude.toString(),
      incidentId: incident.id,
      pinId: pinId,
      trackId: trackId
    },
    messageType: 'pin',
    seenBy: [
      {
        userId: userData?.id || '',
        seenAt: timestamp
      }
    ],
    senderId: userData?.id,
    senderName: userData?.displayName,
    senderProfilePic: userData?.profileUrl,
    timestamp: timestamp,
    isEdited: null,
    reactions: [],
    replyId: null,
    starredBy: []
  }
}

export const updatePinMessageInChat = async (chatId, pinId, updatedData, messageLabel = 'Sent Track Marker') => {
  try {
    const messagesRef = collection(db, 'chats', chatId, 'messages')

    const q = query(
      messagesRef,
      where('pinData.pinId', '==', pinId),
      where('message', '==', messageLabel)
    )
    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return "NOT_FOUND" // Special value to indicate no existing message
    }

    const messageDoc = snapshot.docs[0]
    const messageRef = doc(db, 'chats', chatId, 'messages', messageDoc.id)
    
    // Check if files are null or empty (meaning all attachments removed)
    const hasNoAttachments = !updatedData.files || updatedData.files.attachments.length === 0

    if (hasNoAttachments) {
      // If no attachments, delete the entire message
      await deleteDoc(messageRef)

      // After deletion, find the new last message and update the chat document
      const chatRef = doc(db, 'chats', chatId)
      const messagesQuery = query(
        messagesRef,
        orderBy('timestamp', 'desc'),
        limit(1)
      )
      const lastMessageSnapshot = await getDocs(messagesQuery)

      if (!lastMessageSnapshot.empty) {
        const lastMessage = lastMessageSnapshot.docs[0].data()
        await updateDoc(chatRef, {
          lastMessage: lastMessage.message,
          lastMessageId: lastMessage.messageId,
          lastMessageSenderId: lastMessage.senderId,
          lastUpdatedAt: lastMessage.timestamp,
          updateTimestamp: new Date().toISOString()
        })
      } else {
        // No messages left, clear chat metadata
        await updateDoc(chatRef, {
          lastMessage: null,
          lastMessageId: null,
          lastMessageSenderId: null,
          lastUpdatedAt: new Date().toISOString(),
          updateTimestamp: new Date().toISOString()
        })
      }

      return "DELETED" // Return special value to indicate deletion
    } else {
      // If there are attachments, update the message
      const now = new Date().toISOString()
      await updateDoc(messageRef, {
        ...updatedData,
        timestamp: now
      })

      // Update chat's updateTimestamp as well
      await updateDoc(doc(db, 'chats', chatId), {
        updateTimestamp: now,
        lastUpdatedAt: now,
        lastMessage: updatedData.message || messageLabel,
        lastMessageId: messageDoc.id,
      })

      return messageDoc.id
    }
  } catch (error) {
    console.error('Error updating pin message:', error)
    throw error
  }
}

export const deletePinMessageFromChat = async (chatId, pinId) => {
  try {
    const messagesRef = collection(db, 'chats', chatId, 'messages')

    const q = query(
      messagesRef,
      where('pinData.pinId', '==', pinId)
    )
    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return "NOT_FOUND"
    }

    // Delete all messages associated with this pin
    const batchPromises = snapshot.docs.map(async (messageDoc) => {
      const messageRef = doc(db, 'chats', chatId, 'messages', messageDoc.id)
      return deleteDoc(messageRef)
    })

    await Promise.all(batchPromises)

    // Update chat last message metadata if necessary
    const chatRef = doc(db, 'chats', chatId)
    const messagesQuery = query(
      messagesRef,
      orderBy('timestamp', 'desc'),
      limit(1)
    )
    const lastMessageSnapshot = await getDocs(messagesQuery)

    if (!lastMessageSnapshot.empty) {
      const lastMessage = lastMessageSnapshot.docs[0].data()
      await updateDoc(chatRef, {
        lastMessage: lastMessage.message,
        lastMessageId: lastMessage.messageId,
        lastMessageSenderId: lastMessage.senderId,
        lastUpdatedAt: lastMessage.timestamp,
        updateTimestamp: new Date().toISOString()
      })
    } else {
      await updateDoc(chatRef, {
        lastMessage: null,
        lastMessageId: null,
        lastMessageSenderId: null,
        lastUpdatedAt: new Date().toISOString(),
        updateTimestamp: new Date().toISOString()
      })
    }

    return true
  } catch (error) {
    console.error('Error deleting pin message from chat:', error)
    throw error
  }
}
